import React from 'react'
import Button from "../Button";
import {useQuery} from "react-query";
import {RefreshIcon} from "@heroicons/react/solid";

// Types ----

type CommitSHA = {
  main: string | null
  release: string | null
};

// Constants ----

const FETCH_URL = 'https://operator-app-retrieve-commit-sha.n-gage.workers.dev';
const TEN_MINS_IN_MS = 1000 * 60 * 10;

// Helpers ----

async function fetchCommitSHA(): Promise<CommitSHA | null | void> {
  try {
    const response = await fetch(FETCH_URL);
    return response.ok ? await response.json() : null;
  } catch (error) {
    console.error('Could not fetch commit SHA: ', error);
  }
}

// Component ----

function AppUpdateAlert() {
  // Check if we're in development or staging/production'
  const isDevelopmentEnv = process.env.NODE_ENV === 'development';
  const domain = window.location.hostname;
  const isStagingDomain = domain.includes('n-gage.dev');
  const isProductionDomain = domain.includes('n-gage.io');

  // Fetch git commit SHA data
  const {data} = useQuery(['git-commit-sha'], fetchCommitSHA, {
    enabled: !isDevelopmentEnv,
    refetchInterval: TEN_MINS_IN_MS,
    refetchIntervalInBackground: false,
  });

  // Commit SHA data
  const mainSha = data?.main;
  const releaseSha = data?.release;

  // Setup variables to determine if update is available
  const currentCommitSHA = process.env.REACT_APP_GIT_COMMIT_SHA;
  const hasStagingUpdate = mainSha && currentCommitSHA !== mainSha;
  const hasProductionUpdate = releaseSha && currentCommitSHA !== releaseSha;

  // Return nothing if in development or no update available on staging/production
  if (isDevelopmentEnv) return null;
  if (isStagingDomain && !hasStagingUpdate) return null;
  if (isProductionDomain && !hasProductionUpdate) return null;

  // Return alert if an update is available
  return (
    <div className='flex gap-5 items-center justify-center bg-brand text-white text-sm px-4 py-3 sticky top-0 z-50'>
      <p>A platform update is available</p>

      <Button
        color={Button.colors.WHITE}
        size={Button.sizes.TINY}
        onClick={() => window.location.reload()}
        icon={RefreshIcon}
      >
        Reload
      </Button>
    </div>
  );
}

export {AppUpdateAlert};